import * as React from "react";
import Box from "@mui/joy/Box";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";

import { ApiError, fetchAuthCheck } from "../api";
import { useUserStore } from "../store/useUserStore";

export default function AuthCheckGate({ children }: React.PropsWithChildren) {
  const setIsAuthorized = useUserStore((state) => state.setIsAuthorized);
  const setUser = useUserStore((state) => state.setUser);
  const [status, setStatus] = React.useState<"checking" | "authorized" | "error">("checking");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);
  const signInUrl = React.useMemo(() => {
    const authWebUrl = process.env.REACT_APP_AUTH_WEB_URL ?? "";

    if (typeof window === "undefined" || !window.location) {
      return authWebUrl ? `${authWebUrl}/signin` : "/signin";
    }

    const redirect = encodeURIComponent(window.location.href);
    return authWebUrl ? `${authWebUrl}/signin?redirect=${redirect}` : `/signin?redirect=${redirect}`;
  }, []);

  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const verify = async () => {
      setStatus("checking");
      setErrorMessage(null);

      try {
        const currentUser = await fetchAuthCheck({ signal: controller.signal });
        if (!isMounted) return;
        setUser(currentUser);
        setIsAuthorized(true);
        setStatus("authorized");
      } catch (error) {
        if (!isMounted) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        setIsAuthorized(false);
        setUser(null);

        if (error instanceof ApiError && error.status === 401) {
          // Redirect triggered by apiFetch; keep loading state until navigation happens.
          setStatus("checking");
          return;
        }

        let message: string | null = null;

        if (error instanceof ApiError) {
          if (error.status >= 500) {
            message = 'We could not reach the seva server to confirm your sign-in. Please try again.';
          } else if (error.message && error.message !== 'Unauthorized') {
            message = error.message;
          }
        } else if (error instanceof Error) {
          message = error.message;
        }

        if (!message || /body stream already read/i.test(message)) {
          message = 'We could not verify your sign-in status. Please try again.';
        }

        setErrorMessage(message);
        setStatus("error");
      }
    };

    verify();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [attempt, setIsAuthorized, setUser]);

  if (status === "checking") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.body",
        }}
      >
        <CircularProgress size="md" aria-label="Verifying authentication" />
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.body",
          px: 2,
        }}
      >
        <Sheet
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 420,
            p: 3,
            borderRadius: "lg",
            textAlign: "center",
          }}
        >
          <Stack spacing={2} alignItems="center">
            <Typography level="h4">Authentication required</Typography>
            <Typography level="body-md" sx={{ color: "text.secondary" }}>
              {errorMessage ?? "We couldn't confirm your sign-in status."}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: "100%" }}>
              <Button fullWidth variant="solid" onClick={() => setAttempt((value) => value + 1)}>
                Retry verification
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="neutral"
                onClick={() => {
                  window.location.href = signInUrl;
                }}
              >
                Go to sign-in
              </Button>
            </Stack>
          </Stack>
        </Sheet>
      </Box>
    );
  }

  return <>{children}</>;
}
